import { useEffect, useMemo, useState } from "react";
import type { User } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";

import { db } from "../firebase/client";
import {
  buildInitialUsageState,
  getRemainingUsage,
  normalizeUsagePlanTier,
  normalizeUsageState,
  resolveUsageLimits,
} from "../services/usageLimits";

export function useUsageLimits(user: User | null, fallbackTier?: unknown) {
  const [loadingUsage, setLoadingUsage] = useState<boolean>(!!user);
  const [usage, setUsage] = useState(buildInitialUsageState());
  const [limits, setLimits] = useState(
    resolveUsageLimits(null, normalizeUsagePlanTier(fallbackTier))
  );
  const [tier, setTier] = useState(normalizeUsagePlanTier(fallbackTier));

  useEffect(() => {
    if (!user) {
      setUsage(buildInitialUsageState());
      setTier(normalizeUsagePlanTier(fallbackTier));
      setLimits(resolveUsageLimits(null, fallbackTier));
      setLoadingUsage(false);
      return;
    }

    setLoadingUsage(true);
    const ref = doc(db, "users", user.uid);

    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.exists() ? snap.data() : null;
        const nextTier = normalizeUsagePlanTier(
          data?.plan?.tier ?? data?.planTier ?? data?.tier ?? data?.role ?? fallbackTier
        );

        setTier(nextTier);
        setUsage(normalizeUsageState(data?.usage));
        setLimits(resolveUsageLimits(data, nextTier));
        setLoadingUsage(false);
      },
      (error) => {
        console.error("Erro ao carregar uso do usuario:", error);
        setTier(normalizeUsagePlanTier(fallbackTier));
        setUsage(buildInitialUsageState());
        setLimits(resolveUsageLimits(null, fallbackTier));
        setLoadingUsage(false);
      }
    );

    return () => unsub();
  }, [user, fallbackTier]);

  const remaining = useMemo(() => getRemainingUsage(limits, usage), [limits, usage]);

  return {
    usage,
    limits,
    remaining,
    tier,
    loadingUsage,
  };
}
